// Client for the language-image-server process running on the Mac mini.
//
// Why this exists in a separate module: the GraphQL server on Render has
// ephemeral disk, so image bytes can't live there. The browser uploads PNGs
// directly to language-image-server (over Cloudflare tunnel in prod) and
// passes only the resulting URL to GraphQL. This keeps Render stateless and
// makes mnemonics survive every redeploy.
//
// Env vars:
//   REACT_APP_IMAGE_STORE_URI   base URL, e.g. https://imagestore.skibidi.boo
//   REACT_APP_IMAGE_STORE_TOKEN bearer token matching the server's UPLOAD_TOKEN

const STORE_URI = process.env.REACT_APP_IMAGE_STORE_URI || 'http://localhost:4001'
const STORE_TOKEN = process.env.REACT_APP_IMAGE_STORE_TOKEN || ''

function authHeaders() {
  return STORE_TOKEN ? { Authorization: `Bearer ${STORE_TOKEN}` } : {}
}

// Upload a base64 PNG to the image-store. Returns the public URL.
//
// `kind` is reserved for future asset types (sentences, paragraphs); today
// only 'word' is accepted by the server. `text`/`lang`/`username` are used
// to derive a stable filename hash.
export async function uploadImageToStore({ kind = 'word', text, lang, username, imageBase64 }) {
  if (!text || !lang || !imageBase64) {
    throw new Error('uploadImageToStore: text, lang, imageBase64 are required')
  }
  const res = await fetch(`${STORE_URI}/upload`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...authHeaders(),
    },
    body: JSON.stringify({ kind, text, lang, username, imageBase64 }),
  })
  if (!res.ok) {
    let detail = `HTTP ${res.status}`
    try {
      const body = await res.json()
      detail = body?.error || detail
    } catch (e) { /* not JSON */ }
    throw new Error(`image-store upload failed: ${detail}`)
  }
  const data = await res.json()
  if (!data?.url) throw new Error('image-store upload returned no url')
  return data.url
}

// Best-effort delete. Returns true if the server confirmed deletion; false
// if the file was already gone or the request failed. Never throws — image
// cleanup is decorative, not load-bearing.
export async function deleteImageFromStore(url) {
  if (!url) return false
  try {
    const res = await fetch(`${STORE_URI}/delete`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...authHeaders(),
      },
      body: JSON.stringify({ url }),
    })
    if (!res.ok) return false
    const data = await res.json()
    return !!data?.deleted
  } catch (e) {
    console.warn('deleteImageFromStore failed', e)
    return false
  }
}
